import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Breadcrumbs, Link, Typography, Button } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';

interface StandardBreadcrumbsProps {
  /** Current page title to display in breadcrumbs */
  currentPage: string;
  /** Optional custom back button text (defaults to "BACK") */
  backButtonText?: string;
  /** Optional custom navigation path (defaults to '/') */
  backPath?: string;
}

/**
 * Standardized breadcrumbs and back button component
 * 
 * Provides consistent navigation UI across all detail pages with:
 * - Breadcrumb trail showing "Dashboard / Current Page"
 * - Back button with arrow icon
 * - Consistent styling and spacing
 * 
 * @example
 * ```tsx
 * <StandardBreadcrumbs currentPage="Initiative Details" />
 * <StandardBreadcrumbs currentPage="Team Workload Distribution" backButtonText="BACK TO DASHBOARD" />
 * ```
 */
const StandardBreadcrumbs: React.FC<StandardBreadcrumbsProps> = ({
  currentPage,
  backButtonText = 'BACK',
  backPath = '/'
}) => {
  const navigate = useNavigate();

  return (
    <Box sx={{ mb: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 1.5 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate(backPath)}
          sx={{ 
            textDecoration: 'none',
            color: 'primary.main',
            '&:hover': {
              textDecoration: 'underline'
            }
          }}
        >
          Dashboard
        </Link>
        <Typography color="text.primary">{currentPage}</Typography>
      </Breadcrumbs>

      {/* Back Button */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(backPath)}
          variant="outlined"
          sx={{
            textTransform: 'uppercase',
            fontWeight: 500,
            px: 3,
            py: 1
          }}
        >
          {backButtonText}
        </Button>
      </Box>
    </Box>
  );
};

export default StandardBreadcrumbs;

